import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import request from "@/lib/api/handler";
import type { PayloadAction } from "@reduxjs/toolkit";
import networkErrorHandler from "../../utils/networkErrorHandler";
import { User } from "./authSlice";

export interface Executive {
  id: string;
  name?: string;
  profilePicture?: string;
  role?: string;
  engagementType?: string;
  experience?: string;
  startDate?: string;
  linkedIn?: string;
  cultureLink?: string;
  biography?: string;
}

type ExecutiveAddRequest = Omit<Executive, "id" | "cultureLink">;

export interface Company {
  id?: string;
  name?: string;
  location?: string;
  logoLink?: string;
  foundedYear?: string;
  companySize?: string;
  fundsRaised?: string;
  lastFundraise?: string;
  cultureStatements?: string;
  linkedIn?: string;
  websiteLink?: string;
  aboutUs?: string;
  missionStatement?: string;
  profileComplete?: boolean;
  onboardingComplete?: boolean;
  researchInProgress?: boolean;
  executiveTeam: Executive[];
}

export type CompanyAddRequest = Omit<Company, "id" | "executiveTeam">;

export const companyUserAccountTypeList = ["Admin", "Collaborator"] as const;
export type CompanyUserAccountType =
  (typeof companyUserAccountTypeList)[number];

export interface CompanyClaimAvailable {
  name?: string;
  logoLink?: string;
  available: boolean;
}

export const getCompanyProfile = createAsyncThunk(
  "companyProfile/get",
  async (
    {
      id,
      signal,
    }: {
      id: string;
      signal: AbortSignal;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Company>({
        method: "GET",
        path: `/api/company/${id}`,
        signal,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const updateCompanyProfile = createAsyncThunk(
  "companyProfile/update",
  async (
    {
      id,
      data,
      token,
    }: {
      id: string;
      data: CompanyAddRequest | FormData;
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Partial<Company>>({
        method: "PATCH",
        path: `/api/company/${id}`,
        body: data instanceof FormData ? data : JSON.stringify(data),
        contentType:
          data instanceof FormData ? "multipart/form-data" : "application/json",
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const addExecutive = createAsyncThunk(
  "companyProfile/update",
  async (
    {
      id,
      data,
      token,
    }: {
      id: string;
      data: ExecutiveAddRequest | FormData;
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Partial<Company>>({
        method: "POST",
        path: `/api/company/${id}/executive`,
        body: data instanceof FormData ? data : JSON.stringify(data),
        contentType:
          data instanceof FormData ? "multipart/form-data" : "application/json",
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const updateExecutive = createAsyncThunk(
  "companyProfile/update",
  async (
    {
      companyId,
      executiveId,
      data,
      token,
    }: {
      companyId: string;
      executiveId: string;
      data: ExecutiveAddRequest | FormData;
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Partial<Company>>({
        method: "PATCH",
        path: `/api/company/${companyId}/executive/${executiveId}`,
        body: data instanceof FormData ? data : JSON.stringify(data),
        contentType:
          data instanceof FormData ? "multipart/form-data" : "application/json",
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const reorderExecutives = createAsyncThunk(
  "companyProfile/update",
  async (
    {
      companyId,
      data,
      token,
    }: {
      companyId: string;
      data: { orderedExecutiveIds: string[] };
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Partial<Company>>({
        method: "POST",
        path: `/api/company/${companyId}/executive/reorder`,
        body: JSON.stringify(data),
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const deleteExecutive = createAsyncThunk(
  "companyProfile/update",
  async (
    {
      companyId,
      executiveId,
      token,
    }: {
      companyId: string;
      executiveId: string;
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<Partial<Company>>({
        method: "DELETE",
        path: `/api/company/${companyId}/executive/${executiveId}`,
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const getCompanyClaimStatus = createAsyncThunk(
  "claim/get",
  async (
    {
      id,
      signal,
    }: {
      id: string;
      signal: AbortSignal;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<CompanyClaimAvailable>({
        method: "GET",
        path: `/api/company/${id}/available`,
        signal,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

export const claimCompanyProfile = createAsyncThunk(
  "claim/post",
  async (
    {
      id,
      token,
    }: {
      id: string;
      token: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return request<User>({
        method: "POST",
        path: `/api/company/${id}/claim`,
        token,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        return networkErrorHandler(error, rejectWithValue);
      }
    }
  },
);

// Define a type for the slice state
interface CompanyState {
  company: Company;
  companyClaim?: CompanyClaimAvailable;
  status: "idle" | "pending" | "succeeded" | "failed";
  postingStatus: "idle" | "posting" | "succeeded" | "failed";
}

// Define the initial state using that type
const initialState: CompanyState = {
  company: {
    executiveTeam: [],
  },
  status: "idle",
  postingStatus: "idle",
};

export const companyProfileSlice = createSlice({
  name: "companyProfile",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    resetPostingStatus: (state) => {
      state.postingStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyProfile.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        getCompanyProfile.fulfilled,
        (state, action: PayloadAction<Company | undefined>) => {
          state.company = {
            executiveTeam: [],
            ...action.payload,
          };
          state.status = "succeeded";
        },
      )
      .addCase(getCompanyProfile.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(updateCompanyProfile.pending, (state) => {
        state.postingStatus = "posting";
      })
      .addCase(
        updateCompanyProfile.fulfilled,
        (state, action: PayloadAction<Partial<Company> | undefined>) => {
          state.company = {
            ...state.company,
            ...action.payload,
          };
          state.postingStatus = "succeeded";
        },
      )
      .addCase(updateCompanyProfile.rejected, (state) => {
        state.postingStatus = "failed";
      })
      .addCase(getCompanyClaimStatus.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        getCompanyClaimStatus.fulfilled,
        (state, action: PayloadAction<CompanyClaimAvailable | undefined>) => {
          state.companyClaim = action.payload;
          state.status = "succeeded";
        },
      )
      .addCase(getCompanyClaimStatus.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(claimCompanyProfile.pending, (state) => {
        state.postingStatus = "posting";
      })
      .addCase(claimCompanyProfile.fulfilled, (state) => {
        state.postingStatus = "succeeded";
      })
      .addCase(claimCompanyProfile.rejected, (state) => {
        state.postingStatus = "failed";
      });
  },
});

export const { resetPostingStatus } = companyProfileSlice.actions;

export default companyProfileSlice.reducer;
